import React from "react";

interface AdverseEventStepperProps {
  activeTab: "event_details" | "followups" | "medications" | "pi_assessment";
  isEventDetailsSaved?: boolean;
  onStepClick?: (tab: "event_details" | "followups" | "medications" | "pi_assessment") => void;
  isEditMode?: boolean;
}

const AdverseEventStepper: React.FC<AdverseEventStepperProps> = ({
  activeTab,
  isEventDetailsSaved = false,
  onStepClick,
  isEditMode = false,
}) => {
  const getStepStatus = (step: number) => {
    if (step === 1) return "active";
    if (step === 2) {
      return ["followups", "medications", "pi_assessment"].includes(activeTab) ? "active" : "inactive";
    }
    if (step === 3) {
      return ["medications", "pi_assessment"].includes(activeTab) ? "active" : "inactive";
    }
    if (step === 4) {
      return activeTab === "pi_assessment" ? "active" : "inactive";
    }
    return "inactive";
  };

  const handleStepClick = (tab: "event_details" | "followups" | "medications" | "pi_assessment", step: number) => {
    if (!onStepClick) return;

    // In edit mode allow free navigation between steps by clicking the stepper
    if (isEditMode) {
      onStepClick(tab);
      return;
    }

    // In add mode preserve previous guarded behavior
    if (step === 1) onStepClick("event_details");
    if (step === 2 && (isEventDetailsSaved || ["followups", "medications", "pi_assessment"].includes(activeTab))) {
      onStepClick("followups");
    }
    if (step === 3 && (isEventDetailsSaved || ["medications", "pi_assessment"].includes(activeTab))) {
      onStepClick("medications");
    }
    if (step === 4 && (isEventDetailsSaved || activeTab === "pi_assessment")) {
      onStepClick("pi_assessment");
    }
  };

  const step1Status = getStepStatus(1);
  const step2Status = getStepStatus(2);
  const step3Status = getStepStatus(3);
  const step4Status = getStepStatus(4);

  return (
    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "12px", border: "1px solid #f1f5f9", padding: "1.25rem 2rem" }}>
      <div className="d-flex align-items-center justify-content-between">
        {/* Step 1 */}
        <div
          className="d-flex align-items-center gap-2"
          onClick={() => handleStepClick("event_details", 1)}
          style={{ cursor: isEditMode ? "pointer" : "default" }}
        >
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              backgroundColor: "rgba(6, 182, 212, 0.08)",
              border: "2px solid #06b6d4",
              color: "#06b6d4",
            }}
          >
            <i className="ri-pulse-line"></i>
          </div>
          <span className="fw-semibold" style={{ fontSize: "14px", color: "#06b6d4" }}>
            Event details
          </span>
        </div>

        {/* Line 1 */}
        <div
          style={{
            flex: 1,
            height: "2px",
            backgroundColor: step2Status === "active" ? "#06b6d4" : "#e2e8f0",
            margin: "0 1rem",
            transition: "background-color 0.3s ease",
          }}
        ></div>

        {/* Step 2 */}
        <div
          className="d-flex align-items-center gap-2"
          onClick={() => handleStepClick("followups", 2)}
          style={{
            cursor: isEditMode ? "pointer" : "default",
            opacity: step2Status === "active" ? 1 : 0.6,
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              backgroundColor: step2Status === "active" ? "rgba(6, 182, 212, 0.08)" : "#f8fafc",
              border: step2Status === "active" ? "2px solid #06b6d4" : "2px solid #e2e8f0",
              color: step2Status === "active" ? "#06b6d4" : "#94a3b8",
              transition: "all 0.3s ease",
            }}
          >
            <i className="ri-file-list-3-line"></i>
          </div>
          <span
            className="fw-semibold"
            style={{
              fontSize: "14px",
              color: step2Status === "active" ? "#06b6d4" : "#64748b",
            }}
          >
            Follow-ups
          </span>
        </div>

        {/* Line 2 */}
        <div
          style={{
            flex: 1,
            height: "2px",
            backgroundColor: step3Status === "active" ? "#06b6d4" : "#e2e8f0",
            margin: "0 1rem",
            transition: "background-color 0.3s ease",
          }}
        ></div>

        {/* Step 3 */}
        <div
          className="d-flex align-items-center gap-2"
          onClick={() => handleStepClick("medications", 3)}
          style={{
            cursor: isEditMode ? "pointer" : "default",
            opacity: step3Status === "active" ? 1 : 0.6,
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              backgroundColor: step3Status === "active" ? "rgba(6, 182, 212, 0.08)" : "#f8fafc",
              border: step3Status === "active" ? "2px solid #06b6d4" : "2px solid #e2e8f0",
              color: step3Status === "active" ? "#06b6d4" : "#94a3b8",
              transition: "all 0.3s ease",
            }}
          >
            <i className="ri-capsule-line"></i>
          </div>
          <span
            className="fw-semibold"
            style={{
              fontSize: "14px",
              color: step3Status === "active" ? "#06b6d4" : "#64748b",
            }}
          >
            Medications
          </span>
        </div>

        {/* Line 3 */}
        <div
          style={{
            flex: 1,
            height: "2px",
            backgroundColor: step4Status === "active" ? "#06b6d4" : "#e2e8f0",
            margin: "0 1rem",
            transition: "background-color 0.3s ease",
          }}
        ></div>

        {/* Step 4 */}
        <div
          className="d-flex align-items-center gap-2"
          onClick={() => handleStepClick("pi_assessment", 4)}
          style={{
            cursor: isEditMode ? "pointer" : "default",
            opacity: step4Status === "active" ? 1 : 0.6,
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: "30px",
              height: "30px",
              borderRadius: "50%",
              backgroundColor: step4Status === "active" ? "rgba(6, 182, 212, 0.08)" : "#f8fafc",
              border: step4Status === "active" ? "2px solid #06b6d4" : "2px solid #e2e8f0",
              color: step4Status === "active" ? "#06b6d4" : "#94a3b8",
              transition: "all 0.3s ease",
            }}
          >
            <i className="ri-shield-user-line"></i>
          </div>
          <span
            className="fw-semibold"
            style={{
              fontSize: "14px",
              color: step4Status === "active" ? "#06b6d4" : "#64748b",
            }}
          >
            PI Assessment
          </span>
        </div>
      </div>
    </div>
  );
};

export default AdverseEventStepper;
